// Composer-side "Save as template" dialog.
//
// Captures the current rich-editor body as a new reply_templates row
// (migration 021), with the visibility model from migration 053:
//
//   * Shared  — visible + editable to every active team member.
//   * Private — visible + editable only to you.
//
// Naming uniqueness lives in the DB (partial unique indexes per
// visibility). The same name can collide across the two namespaces;
// the slash menu shows a "(private)" tag so the user knows which one
// they're picking.
//
// Variable hints: the existing slash-menu's `applyVariables()` runs
// substitution at insert time, so users who write `{{first_name}}`
// etc. into the body here get dynamic values on insert later. We
// surface a one-line hint pointing at the supported placeholders.

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { toastError } from '@/lib/toast';
import { Loader2, BookmarkPlus, Users, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useUpsertReplyTemplate,
  type ReplyTemplateVisibility,
} from '@/lib/queries';
import { useAuth } from '@/lib/auth-guard';
import { cn } from '@/lib/utils';
import { uploadInlineTemplateImages } from '@/lib/template-inline-images';

/** Quick + dirty HTML → plain text for the preview. Mirrors the
 *  composer's own `htmlToPlain` (kept local so the dialog has no
 *  cross-file dependency on the composer internals). */
function htmlToPlain(html: string): string {
  if (typeof window === 'undefined') return html;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.innerText || tmp.textContent || '').trim();
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rich-editor HTML body. Stored verbatim as the template body so
   *  the formatting + any `{{variable}}` placeholders the agent typed
   *  survive into future inserts. */
  bodyHtml: string;
  /** Called with the post-upload rewritten HTML so the parent composer
   *  can swap its editor's blob: image refs to permanent storage URLs
   *  too. Without this callback, the parent's editor still holds the
   *  blob: src after save, and a subsequent send finds nothing in the
   *  pending-images registry (we already consumed it for the template)
   *  so the recipient gets a broken image. Optional because not every
   *  caller mounts this dialog over a live editor. */
  onBodyHtmlRewritten?: (rewrittenHtml: string) => void;
}

export function SaveAsTemplateDialog({ open, onOpenChange, bodyHtml, onBodyHtmlRewritten }: Props) {
  const { teamMember } = useAuth();
  const upsert = useUpsertReplyTemplate();

  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<ReplyTemplateVisibility>('shared');

  // Reset on open so reopening doesn't show the previous attempt's
  // half-typed name. Body comes from the parent each time — it's
  // already up-to-date.
  useEffect(() => {
    if (open) {
      setName('');
      setVisibility('shared');
    }
  }, [open]);

  // Preview = first ~280 chars of plain text. Just for the agent to
  // verify the dialog captured what they expected.
  const preview = useMemo(() => {
    const text = htmlToPlain(bodyHtml);
    return text.length > 280 ? text.slice(0, 280) + '…' : text;
  }, [bodyHtml]);

  const bodyEmpty = preview.length === 0;
  const valid = name.trim().length > 0 && !bodyEmpty;

  async function submit() {
    if (!valid) return;
    if (!teamMember?.slack_user_id) {
      toast.error('Could not resolve your team member row.');
      return;
    }
    try {
      // Upload any pasted-image blob: refs in the body to permanent
      // Supabase Storage URLs and rewrite the HTML before we save. Without
      // this, the template body would carry a session-only blob URL that
      // breaks the moment the browser tab closes. Errors surface as a
      // toast and ABORT the save — better to fail loudly than silently
      // ship a broken template.
      const bodyForSave = await uploadInlineTemplateImages(bodyHtml);
      // Tell the parent composer about the rewrite so its editor's
      // blob: srcs get swapped to permanent URLs too — otherwise the
      // next send finds nothing to upload (pending-images already
      // consumed) and the recipient gets a broken image.
      if (bodyForSave !== bodyHtml) onBodyHtmlRewritten?.(bodyForSave);
      await upsert.mutateAsync({
        name: name.trim(),
        body: bodyForSave,
        visibility,
        created_by_slack_id: teamMember.slack_user_id,
      });
      toast.success(
        visibility === 'private'
          ? `Saved as private template: ${name.trim()}`
          : `Saved as shared template: ${name.trim()}`,
      );
      onOpenChange(false);
    } catch (e) {
      // Most common failure: name collides with another template in
      // the same visibility scope (partial unique index from migration
      // 053). Surface the DB error as-is — it's usually self-explanatory.
      toastError('Save failed', e);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4 text-foreground/70" />
            Save reply as template
          </DialogTitle>
          <DialogDescription>
            Reuse this reply later from the composer's <kbd className="rounded border border-border/60 bg-muted px-1 py-px text-3xs font-mono">/</kbd> menu. You can edit it any time on the Templates page.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div>
            <Label htmlFor="tpl-name" className="text-xs">
              Template name
            </Label>
            <Input
              id="tpl-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "POD password reset walkthrough"'
              className="mt-1 h-8 text-sm"
              maxLength={120}
            />
            <p className="mt-1 text-3xs text-muted-foreground">
              You'll type{' '}
              <kbd className="rounded border border-border/60 bg-muted px-1 py-px font-mono">/{name.trim().toLowerCase().slice(0, 20) || 'name'}</kbd>{' '}
              in the composer to insert this template.
            </p>
          </div>

          <div>
            <Label className="text-xs">Visibility</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setVisibility('shared')}
                className={cn(
                  'flex items-start gap-2 rounded-md border p-2.5 text-left transition-colors',
                  visibility === 'shared'
                    ? 'border-foreground/40 bg-accent'
                    : 'border-border/60 hover:bg-accent/40',
                )}
              >
                <Users className="mt-0.5 h-4 w-4 shrink-0 text-foreground/70" />
                <div className="min-w-0">
                  <div className="text-sm font-medium">Shared</div>
                  <div className="text-3xs leading-snug text-muted-foreground">
                    Everyone on the team can see + edit it.
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setVisibility('private')}
                className={cn(
                  'flex items-start gap-2 rounded-md border p-2.5 text-left transition-colors',
                  visibility === 'private'
                    ? 'border-foreground/40 bg-accent'
                    : 'border-border/60 hover:bg-accent/40',
                )}
              >
                <UserIcon className="mt-0.5 h-4 w-4 shrink-0 text-foreground/70" />
                <div className="min-w-0">
                  <div className="text-sm font-medium">Private</div>
                  <div className="text-3xs leading-snug text-muted-foreground">
                    Only you can see + edit it.
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Preview</Label>
            <div className="mt-1 max-h-32 overflow-y-auto rounded-md border border-border/60 bg-muted/30 p-2 text-2xs leading-relaxed text-muted-foreground">
              {bodyEmpty ? (
                <em>(empty — type something in the composer first)</em>
              ) : (
                <span className="whitespace-pre-wrap">{preview}</span>
              )}
            </div>
            <p className="mt-1 text-3xs text-muted-foreground">
              Tip: use{' '}
              <code className="rounded bg-muted px-1 font-mono">{'{{first_name}}'}</code>,{' '}
              <code className="rounded bg-muted px-1 font-mono">{'{{ticket_subject}}'}</code>,{' '}
              <code className="rounded bg-muted px-1 font-mono">{'{{sender_email}}'}</code>,{' '}
              <code className="rounded bg-muted px-1 font-mono">{'{{ticket_id_short}}'}</code>{' '}
              — they fill in on insert.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={upsert.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!valid || upsert.isPending}>
            {upsert.isPending ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <BookmarkPlus className="mr-1 h-3.5 w-3.5" />
                Save template
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
